import { Plus, Trash2 } from "lucide-react";
import { LGoButton } from "@/ui/branded";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Switch } from "@/ui/primitives/switch";
import { customFieldsLimit, type CustomFieldType } from "@/domain/custom-fields/types";
import { customFieldsCopy } from "@/features/custom-fields/custom-fields-copy";
import {
  customFieldLabelMaxLength,
  newCustomFieldRow,
  withLabel,
  type CustomFieldRow,
} from "@/features/custom-fields/form-schema";

interface CustomFieldsEditorProps {
  rows: CustomFieldRow[];
  onChange: (rows: CustomFieldRow[]) => void;
  disabled?: boolean;
}

export function CustomFieldsEditor({ rows, onChange, disabled }: CustomFieldsEditorProps) {
  const canAddField = rows.length < customFieldsLimit;
  const someRowIsSaved = rows.some((row) => row.isSaved);

  const updateRow = (index: number, patch: Partial<CustomFieldRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">{customFieldsCopy.forbiddenTopicsNotice}</p>

      {rows.length === 0 && (
        <p className="text-sm text-muted-foreground">{customFieldsCopy.emptyEditor}</p>
      )}

      {rows.map((row, index) => {
        const position = index + 1;
        return (
          <div key={index} className="rounded-lg border border-border p-3 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {customFieldsCopy.fieldOrdinal(position)}
              </span>
              <LGoButton
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 shrink-0"
                aria-label={customFieldsCopy.removeField(position)}
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
                disabled={disabled}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </LGoButton>
            </div>

            <div className="flex items-start gap-2">
              <Input
                aria-label={customFieldsCopy.labelAria(position)}
                className="text-base font-medium"
                placeholder={customFieldsCopy.labelPlaceholder}
                maxLength={customFieldLabelMaxLength}
                value={row.label}
                onChange={(e) => onChange(withLabel(rows, index, e.target.value))}
                disabled={disabled}
              />
              <Select
                value={row.type}
                onValueChange={(type) => updateRow(index, { type: type as CustomFieldType })}
                disabled={disabled}
              >
                <SelectTrigger className="w-32" aria-label={customFieldsCopy.typeAria(position)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">{customFieldsCopy.typeText}</SelectItem>
                  <SelectItem value="number">{customFieldsCopy.typeNumber}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <Switch
                  checked={row.required}
                  onCheckedChange={(required) => updateRow(index, { required })}
                  disabled={disabled}
                />
                {customFieldsCopy.requiredToggle}
              </label>

              {row.type === "number" ? (
                <>
                  <BoundInput
                    label={customFieldsCopy.minLabel}
                    ariaLabel={customFieldsCopy.minAria(position)}
                    value={row.min}
                    onChange={(min) => updateRow(index, { min })}
                    disabled={disabled}
                  />
                  <BoundInput
                    label={customFieldsCopy.maxLabel}
                    ariaLabel={customFieldsCopy.maxAria(position)}
                    value={row.max}
                    onChange={(max) => updateRow(index, { max })}
                    disabled={disabled}
                  />
                </>
              ) : (
                <BoundInput
                  label={customFieldsCopy.maxLengthLabel}
                  ariaLabel={customFieldsCopy.maxLengthAria(position)}
                  value={row.maxLength}
                  onChange={(maxLength) => updateRow(index, { maxLength })}
                  disabled={disabled}
                  placeholder={customFieldsCopy.maxLengthPlaceholder}
                />
              )}
            </div>
          </div>
        );
      })}

      {someRowIsSaved && (
        <p className="text-xs text-muted-foreground">{customFieldsCopy.renamingKeepsAnswers}</p>
      )}

      <div className="flex items-center gap-3">
        <LGoButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange([...rows, newCustomFieldRow()])}
          disabled={disabled || !canAddField}
        >
          <Plus className="h-4 w-4" /> {customFieldsCopy.addField}
        </LGoButton>
        <span className="text-xs text-muted-foreground">
          {customFieldsCopy.fieldCount(rows.length, customFieldsLimit)}
        </span>
      </div>
    </div>
  );
}

interface BoundInputProps {
  label: string;
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

function BoundInput({ label, ariaLabel, value, onChange, disabled, placeholder }: BoundInputProps) {
  return (
    <label className="flex items-center gap-2 text-sm text-muted-foreground">
      {label}
      <Input
        aria-label={ariaLabel}
        className="w-24"
        inputMode="numeric"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </label>
  );
}
